// src/components/LeaseStatusBadge.jsx
//
// OFFICE-OVERVIEW-LEASE-STATUS-CONTROL-S1
//
// Pill displaying a lease status label. Structurally identical to StageBadge:
//   - Display-only path (`canWrite=false` or no `onChange`): a static `<div>` pill.
//   - Editable path (`canWrite=true` AND `onChange` provided): a `<button>` trigger
//     with a `▾` chevron; clicking opens a listbox dropdown. Selecting an option
//     calls `await onChange(newStatus)`; busy/error states mirror StageBadge.
//
// A11y: Escape closes + returns focus, ArrowDown/ArrowUp navigate options,
// click-outside closes.

import React, { useEffect, useId, useRef, useState } from "react";
import { T } from "../app/tokens.js";
import { S } from "../app/styles.js";
import { LEASE_STATUS_EMPTY, LEASE_STATUS_META, LEASE_STATUS_OPTIONS, LEASE_STATUS_UNKNOWN } from "../features/leases/leaseStatusLabels.js";

export function LeaseStatusBadge({ status, canWrite = false, onChange }) {
  // OFFICE-OVERVIEW-LEASE-STATUS-CONTROL-ALL-OFFICES: null status is a
  // legitimate "no lease record yet" state for a lease-bearing office —
  // show a dedicated settable empty state rather than "Unknown".
  const info     = status == null ? LEASE_STATUS_EMPTY : (LEASE_STATUS_META[status] || LEASE_STATUS_UNKNOWN);
  const editable = canWrite && typeof onChange === "function";

  if (!editable) {
    return (
      <div
        data-testid="lease-status-badge"
        style={{
          display:        "inline-flex",
          alignItems:     "center",
          justifyContent: "center",
          borderRadius:   6,
          padding:        "2px 10px",
          fontSize:       T.fsXs,
          fontWeight:     T.fwSemibold,
          background:     info.bg,
          color:          info.color,
          minWidth:       80,
          whiteSpace:     "nowrap",
        }}
      >
        {info.label}
      </div>
    );
  }

  return <EditableLeaseStatusBadge status={status} info={info} onChange={onChange} />;
}

function EditableLeaseStatusBadge({ status, info, onChange }) {
  const [open, setOpen]   = useState(false);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");
  const triggerRef        = useRef(null);
  const menuRef           = useRef(null);
  const menuId            = useId();

  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        requestAnimationFrame(() => triggerRef.current?.focus());
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      const active = menuRef.current?.querySelector(`[data-testid="lease-status-badge-option-${status}"]`);
      (active || menuRef.current?.querySelector("button"))?.focus();
    });
  }, [open, status]);

  function onMenuKeyDown(e) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const buttons = Array.from(
      menuRef.current?.querySelectorAll("button[data-testid^='lease-status-badge-option-']") || []
    );
    const idx = buttons.indexOf(document.activeElement);
    if (idx < 0) return;
    e.preventDefault();
    const next = e.key === "ArrowDown"
      ? (idx + 1) % buttons.length
      : (idx - 1 + buttons.length) % buttons.length;
    buttons[next]?.focus();
  }

  async function handleSelect(newStatus) {
    if (busy || newStatus === status) {
      if (newStatus === status) {
        setOpen(false);
        requestAnimationFrame(() => triggerRef.current?.focus());
      }
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onChange(newStatus);
      setBusy(false);
      setOpen(false);
      requestAnimationFrame(() => triggerRef.current?.focus());
    } catch (err) {
      setBusy(false);
      setError(mapError(err));
    }
  }

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <button
        ref={triggerRef}
        type="button"
        data-testid="lease-status-badge"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        disabled={busy}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          borderRadius: 6,
          padding: "2px 10px",
          fontSize: T.fsXs,
          fontWeight: T.fwSemibold,
          letterSpacing: 0.3,
          lineHeight: 1,
          whiteSpace: "nowrap",
          background: info.bg,
          color: info.color,
          border: "none",
          cursor: busy ? "wait" : "pointer",
          gap: 4,
          opacity: busy ? 0.6 : 1,
        }}
      >
        {info.label}
        <span aria-hidden="true" style={{ fontSize: T.fsXxxs, opacity: 0.7 }}>▾</span>
      </button>
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="listbox"
          data-testid="lease-status-badge-menu"
          onKeyDown={onMenuKeyDown}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 20,
            minWidth: 160,
            background: T.card,
            border: `1px solid ${T.border}`,
            borderRadius: 6,
            boxShadow: T.cardShadow,
            padding: 4,
          }}
        >
          {LEASE_STATUS_OPTIONS.map(({ value, label }) => {
            const opt    = LEASE_STATUS_META[value] || LEASE_STATUS_UNKNOWN;
            const active = value === status;
            return (
              <button
                key={value}
                type="button"
                role="option"
                aria-selected={active}
                data-testid={`lease-status-badge-option-${value}`}
                onClick={() => handleSelect(value)}
                disabled={busy}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "6px 10px",
                  background: active ? T.bg : "transparent",
                  border: "none",
                  borderRadius: 4,
                  cursor: busy ? "wait" : "pointer",
                  fontSize: T.fsSm,
                  fontWeight: active ? 600 : 500,
                  color: T.dark,
                  textAlign: "left",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: opt.color, flexShrink: 0 }} />
                  {label}
                </span>
                {active && <span aria-hidden="true" style={{ color: T.mid, fontSize: T.fsXs }}>✓</span>}
              </button>
            );
          })}
          {error && (
            <div
              data-testid="lease-status-badge-error"
              role="alert"
              style={{ padding: "6px 10px", fontSize: T.fsXs, color: T.red }}
            >
              {error}
            </div>
          )}
        </div>
      )}
    </span>
  );
}

function mapError(err) {
  const name = err?.name;
  if (name === "AuthError")       return "You don't have permission to change lease status.";
  if (name === "ValidationError") return err.message || "Invalid status.";
  if (name === "NotFoundError")   return "Lease not found.";
  if (name === "NetworkError")    return "Network error. Please try again.";
  return "Couldn't update lease status. Please try again.";
}
